<template>
  <div class="fts-client">
    <vue-headful
      :title="`${module.name || ''} | ${workspace.name || 'Inistate'}`"
    />
    <!-- sidenav -->
    <div
      class="client-sidenav"
      @mouseover="mouseoverSidenav"
      @mouseleave="mouseleaveSidenav"
    >
      <div class="client-sidenav-header">
        <div class="client-sidenav-wrapper">
          <img :src="sidenavLogo" class="client-sidenav-logo" />
        </div>
      </div>
      <div class="client-sidenav-body fts-scrollbar fts-scrollbar-overlay">
        <fts-tippy
          triggerClass="sidenav-menu-outer"
          :content="workspace.name"
          :options="{ arrow: false, placement: 'left' }"
          class="sidenav-menu"
          v-for="workspace in preWorkspaces"
          :key="workspace.id"
        >
          <div
            slot="trigger"
            class="sidenav-menu-wrapper"
            :class="{ active: workspaceId == workspace.id }"
            @click="selectWorkspace(workspace)"
          >
            <div class="sidenav-workspace">{{ workspace.shortName }}</div>
          </div>
        </fts-tippy>
        <!-- notification -->
        <fts-dropdown-select
          append-to-body
          prevent-overflow-alt-axis
          prevent-overflow-tether-off
          :offset="[0, 6]"
          append-toggle-class="sidenav-menu-wrapper"
          content-class="notification-content notification-dropdown-menu"
          class="notif-dropdown sidenav-menu"
          @show="openNotification"
          @selected="openQuickView($event)">
          <div slot="toggle" class="notif-dropdown-toggle">
            <font-awesome-icon
              :icon="['far', 'bell']"
              fixed-width
              size="lg"
              class="sidenav-icon"
            />
            <div class="notif-count" v-if="unreadCount > 0">
              {{ unreadCount }}
            </div>
          </div>
          <template #menu="{select}">
            <!-- notification:header -->
            <div class="notification-dropdown__header">
              <div class="notification-header-title">Notifications</div>
            </div>
            <!-- notification:content -->
            <div class="notification-dropdown__content fts-scrollbar">
              <template v-if="notifications && notifications.length">
                <template v-for="notification in notifications">
                  <!-- group -->
                  <div
                    class="notification-group"
                    :class="{'notification-group--expand': notification.expand}"
                    v-if="notification.isGroup" 
                    :key="notification.id"
                  >
                    <!-- group:toggle -->
                    <div
                      class="notification-group__toggle notification-content"
                      :class="{'notification-content--read': notification.read}"
                      @click="toggleNotificationGroup(notification)"
                    >
                      <div class="notification-group-profile">
                        <fts-moji
                          :size="20"
                          :emoji="moduleData(notification.vectorId).emoji"
                          class="notification-group-title__emoji"
                          v-if="moduleData(notification.vectorId).emoji"
                        />
                        <font-awesome-icon :icon="['far', 'bell']" size="lg" v-else />
                      </div>
                      <div class="notification-content-details">
                        <div class="notification-group-name">
                          {{moduleData(notification.vectorId).name || '(undefined)'}}
                        </div>
                        <div v-if="notificationGroupUnreadCount(notification.list)">
                          {{notificationGroupUnreadCount(notification.list)}}
                        </div>
                        <font-awesome-icon :icon="['fas', 'caret-right']" class="notification-group-toggle" />
                      </div>
                    </div>
                    <!-- group:content -->
                    <fts-transition-expand>
                      <div class="notification-group__content" v-if="notification.expand">
                        <fts-notification-content
                          v-for="x in notification.list"
                          :key="x.id"
                          :notification="x"
                          @select="select"
                        />
                      </div>
                    </fts-transition-expand>
                  </div>
                  <!-- single -->
                  <fts-notification-content
                    :key="notification.id"
                    :notification="notification"
                    @select="select"
                    v-else
                  />
                </template>
              </template>
              <!-- infinite loading -->
              <infinite-loading @infinite="infiniteHandler">
                <fts-spinner
                  show
                  slot="spinner"
                  class="notification-loader-spinner"
                />
                <div slot="no-more" class="notification-loader-blank">
                  No older notifications
                </div>
                <div slot="no-results" class="notification-loader-blank">
                  No older notifications
                </div>
              </infinite-loading>
            </div>
          </template>
        </fts-dropdown-select>
        <!-- dashboard -->
        <fts-tippy
          triggerClass="sidenav-menu-outer"
          content="My Dashboard"
          :options="{ arrow: false, placement: 'left' }"
          class="sidenav-menu"
        >
          <router-link
            slot="trigger"
            to="/dashboard"
            custom
            class="sidenav-menu-wrapper"
          >
            <font-awesome-icon
              :icon="['fas', 'th-large']"
              fixed-width
              size="lg"
              class="sidenav-icon"
            />
          </router-link>
        </fts-tippy>
        <!-- calendar -->
        <fts-tippy
          triggerClass="sidenav-menu-outer"
          content="My Calendar"
          :options="{ arrow: false, placement: 'left' }"
          class="sidenav-menu"
        >
          <router-link
            slot="trigger"
            to="/calendar"
            custom
            class="sidenav-menu-wrapper"
          >
            <font-awesome-icon
              :icon="['far', 'calendar']"
              fixed-width
              size="lg"
              class="sidenav-icon"
            />
          </router-link>
        </fts-tippy>
        <!-- users -->
        <fts-tippy
          triggerClass="sidenav-menu-outer"
          content="Users"
          :options="{ arrow: false, placement: 'left' }"
          class="sidenav-menu"
          v-if="isAdminUser || isManagerUser"
        >
          <router-link
            slot="trigger"
            :to="{ name: 'users' }"
            class="sidenav-menu-wrapper"
          >
            <font-awesome-icon
              :icon="['fas', 'user-friends']"
              fixed-width
              size="lg"
              class="sidenav-icon"
            />
          </router-link>
        </fts-tippy>
        <!-- invite -->
        <fts-tippy
          triggerClass="sidenav-menu-outer"
          content="Invite"
          :options="{ arrow: false, placement: 'left' }"
          class="sidenav-menu"
          v-if="isAdminUser || isManagerUser"
        >
          <div
            slot="trigger"
            class="sidenav-menu-wrapper"
            @click="openInvitation"
          >
            <font-awesome-icon
              :icon="['fas', 'user-plus']"
              fixed-width
              size="lg"
              class="sidenav-icon"
            />
          </div>
        </fts-tippy>
      </div>
      <!-- sidenav footer -->
      <div class="client-sidenav-footer">
        <!-- studio -->
        <fts-tippy
          triggerClass="sidenav-menu-outer"
          content="Design"
          :options="{ arrow: false, placement: 'left' }"
          class="sidenav-menu apex"
          v-if="isAdminUser"
        >
          <router-link
            slot="trigger"
            :to="studioRoute"
            class="sidenav-menu-wrapper"
          >
            <font-awesome-icon
              :icon="['fas', 'wand-magic-sparkles']"
              fixed-width
              size="lg"
              class="sidenav-icon"
            />
          </router-link>
        </fts-tippy>
        <!-- profile -->
        <fts-profile
          :profile="profile"
          :profile-photo-size="30"
          :profile-name="false"
          profile-photo-class="user-profile-avatar"
          class="client-user-profile"
          @click.native="openUserProfileModal"
        />
      </div>
    </div>
    <!-- drawer -->
    <div
      class="client-drawer"
      :class="[{ collapsed: !drawer }, { close: !drawer && !drawerToggle }]"
      @mouseover="mouseoverDrawer"
      @mouseleave="mouseleaveDrawer"
    >
      <div class="client-drawer-header">
        <!-- workspace -->
        <fts-dropdown-select
          class="client-dropdown"
          append-toggle-class="client-dropdown-toggle"
          content-class="client-content client-dropdown-menu"
          :offset="[0, 6]"
          @show="showWorkspaceOptions"
          @selected="selectWorkspace"
        >
          <!-- toggle -->
          <template #toggle>
            <div class="client-dropdown-toggle__label">
              {{workspace.name || "Workspace"}}
            </div>
          </template>
          <!-- menu -->
          <template #menu="{ select, defaultOptionClass}">
            <div class="client-dropdown-item">
              <div class="client-dropdown-search">
                <div class="fts-form-control-filter">
                  <input
                    type="text"
                    class="fts-form-control"
                    v-model="workspaceSearch"
                    placeholder="Search workspace"
                    ref="workspaceSearch"
                  />
                  <div class="search-icon">
                    <font-awesome-icon :icon="['fas', 'search']" fixed-width />
                  </div>
                </div>
              </div>
              <template
                v-if="workspaceOptionList && workspaceOptionList.length"
              >
                <div
                  v-for="workspace in workspaceOptionList"
                  :key="workspace.id"
                  :class="defaultOptionClass"
                  @click="select(workspace)"
                >
                  {{ workspace.name }}
                </div>
              </template>
              <div class="client-dropdown-no-found" v-else>
                <div class="client-dropdown-no-found__icon">
                  <font-awesome-icon :icon="['fas', 'search']" />
                </div>
                <div class="client-dropdown-no-found__label">
                  No matching results
                </div>
              </div>
            </div>
          </template>
        </fts-dropdown-select>
        <!-- drawer toggle -->
        <fts-tippy
          triggerClass="client-drawer-toggle"
          :options="{ arrow: false }"
        >
          <template #trigger>
            <font-awesome-icon
              :icon="['fas', 'angle-double-left']"
              fixed-width
              @click="toggleDrawer"
              v-if="drawer"
            />
            <font-awesome-icon
              :icon="['fas', 'thumbtack']"
              fixed-width
              @click="toggleDrawer"
              v-else
            />
          </template>
          <div slot="content">
            {{ drawer ? "close sidebar" : "lock sidebar open" }}
          </div>
        </fts-tippy>
      </div>
      <div class="client-drawer-body fts-scrollbar fts-scrollbar-overlay">
        <!-- context menu -->
        <fts-dropdown-select
          class="workspace-context-dropdown"
          append-toggle-class="workspace-context-toggle"
          content-class="client-content client-dropdown-menu"
          :offset="[0, 6]"
          @show="showContextOptions"
          @selected="selectWorkspaceContext"
          v-if="workspace.hasContext">
          <template #toggle>
            <div class="workspace-context-name">
              {{ workspaceContext.name || "Select Context" }}
            </div>
            <font-awesome-icon
              :icon="['fas', 'chevron-down']"
              class="workspace-context-toggle-icon"
              fixed-width
            />
          </template>
          <template #menu="{ select, defaultOptionClass}">
            <div class="client-dropdown-item">
              <div class="client-dropdown-search">
                <div class="fts-form-control-filter">
                  <input
                    type="text"
                    class="fts-form-control"
                    v-model="workspaceContextSearch"
                    placeholder="Search context"
                    ref="workspaceContextSearch"
                  />
                  <div class="search-icon">
                    <font-awesome-icon :icon="['fas', 'search']" fixed-width />
                  </div>
                </div>
              </div>
              <template v-if="contextOptionList && contextOptionList.length">
                <div
                  v-for="context in contextOptionList"
                  :key="context.id"
                  :class="defaultOptionClass"
                  @click="select(context)"
                >
                  {{ context.name }}
                </div>
              </template>
              <div class="client-dropdown-no-found" v-else>
                <div class="client-dropdown-no-found__icon">
                  <font-awesome-icon :icon="['fas', 'search']" />
                </div>
                <div class="client-dropdown-no-found__label">
                  No matching results
                </div>
              </div>
            </div>
          </template>
        </fts-dropdown-select>
        <!-- search -->
        <!-- <div class="search-wrapper" v-if="isInternal">
          <font-awesome-icon :icon="['fas', 'search']" class="search-icon" />
          <input type="text" placeholder="Search" class="fts-form-control search-input" v-model="drawerSearchKeyword">
        </div> -->
        <!-- module -->
        <fts-module />
      </div>
      <div class="client-drawer-footer">
        <fts-profile
          :profile="profile"
          :profile-photo="false"
          profile-name-class="user-profile-info"
          class="client-user-profile"
          @click.native="openUserProfileModal"
        >
          <template #profileName="{ userProfileName, userProfileUsername }">
            <div class="user-profile-wrapper">
              <div class="user-profile-name">{{ userProfileName }}</div>
              <div class="user-profile-role">{{ userRoles }}</div>
            </div>
            <div
              class="user-profile-username client-drawer-username"
              v-if="userProfileUsername"
            >
              {{ userProfileUsername }}
            </div>
          </template>
        </fts-profile>
      </div>
    </div>
    <!-- content -->
    <div class="client-content">
      <div class="client-broadcast-notification" v-if="broadcastNotification">
        This workspace is updated.
        <span class="fts-link-primary" @click="refresh"> Click to reload</span>
      </div> 
      <router-view :key="routePath" v-if="loaded" />
    </div>
    <!-- confirmation -->
    <v-dialog />
    <!-- user profile -->
    <fts-user-profile client ref="ftsUserProfile" />
    <!-- invitation -->
    <fts-invitation ref="ftsInvitation" />
    <!-- quick view -->
    <fts-quick-view
      :key="`qv-client-${quickViewKey}`"
      @refresh="quickViewRefresh"
      @closed="quickViewKey += 1"
    />
    <!--pdf view-->
    <fts-document-modal ref="documentModal" />
  </div>
</template>

<script>
import Vue from "vue";
import vueHeadful from "vue-headful";
import InfiniteLoading from "vue-infinite-loading";
import VueCookies from "vue-cookies";
Vue.use(VueCookies);
// import VueI18n from 'vue-i18n'
// Vue.use(VueI18n)

import webAppConfig from "./components/commons/webApp.config";
import commons from "./components/commons/commons";
import { classificationStore } from "./components/store/classificationStore";
import { clientStore } from "./components/store/clientStore";
import { authorizationStore } from "./components/store/authorizationStore";

import FTSModule from "./components/design/FTSModule";
import FTSDropdownSelect from "./components/elements/FTSDropdownSelect";
import FTSUserProfile from "./components/elements/FTSUserProfile";
import FTSProfile from "./components/elements/FTSProfile";
import FTSMoji from "./components/elements/FTSMoji";
import FTSBasket from "./components/elements/FTSBasket";
import FTSTippy from "./components/elements/FTSTippy";
import FTSSpinner from "./components/elements/FTSSpinner";
import FTSInvitation from "./components/client/FTSInvitation";
import FTSQuickView from "./components/elements/views/FTSQuickView";
import FTSDocumentModal from "./components/elements/FTSDocumentModal";
import FTSTransitionExpand from "./components/elements/FTSTransitionExpand";
import FTSNotificationContent from "./components/elements/notification/FTSNotificationContent";

import signalr from "./components/store/signalr";


export default {
  components: {
    vueHeadful,
    InfiniteLoading,
    "fts-module": FTSModule,
    "fts-dropdown-select": FTSDropdownSelect,
    "fts-user-profile": FTSUserProfile,
    "fts-profile": FTSProfile,
    "fts-moji": FTSMoji,
    "fts-basket": FTSBasket,
    "fts-tippy": FTSTippy,
    "fts-spinner": FTSSpinner,
    "fts-invitation": FTSInvitation,
    "fts-quick-view": FTSQuickView,
    "fts-document-modal": FTSDocumentModal,
    "fts-transition-expand": FTSTransitionExpand,
    "fts-notification-content": FTSNotificationContent,
  },
  data: () => ({
    loaded: false,
    drawer: true,
    drawerToggle: false,
    drawerSearchKeyword: null,
    sidenavFlag: false,
    /* workspace */
    workspaceSearch: null,
    workspaceOptionList: [],
    workspaceContextSearch: null,
    /* context */
    contextOptionList: [],
    /* notification */
    unreadCount: 0,
    notifications: [],
    notifCurrentPage: 0,
    /* pre workspaces */
    preWorkspaces: [],
    /* quick view */
    quickViewKey: 0,
    /* broadcast */
    broadcastNotification: false,
  }),
  methods: {
    toggleDrawer() {
      this.drawer = !this.drawer;

      if (!this.drawer) this.sidenavFlag = this.drawerToggle = false;
    },
    mouseoverDrawer() {
      this.sidenavFlag
        ? (this.drawerToggle = true)
        : (this.drawerToggle = false);
    },
    mouseleaveDrawer() {
      this.drawerToggle = this.sidenavFlag = false;
    },
    mouseoverSidenav() {
      this.drawerToggle = this.sidenavFlag = true;
    },
    mouseleaveSidenav() {
      this.drawerToggle = false;
    },
    moduleData(moduleId) {
      const module = (clientStore.state.workspaceModules || []).find(
        x => x.id == moduleId
      ) || {};

      return {
        id: module.id,
        name: module.name,
        emoji: module.emoji,
      };
    },
    openInvitation() {
      this.$refs.ftsInvitation.open();
    },
    async openUserProfileModal() {
      await this.$refs.ftsUserProfile.show();
    },
    async showWorkspaceOptions() {
      this.workspaceSearch = "";
      this.workspaceOptionList = await clientStore.dispatch("getWorkspaces", {
        search: this.workspaceSearch,
      });

      this.$nextTick(() => {
        this.$refs.workspaceSearch.focus();
      });
    },
    async selectWorkspace(res) {
      if (res.id != this.workspaceId) {
        await clientStore.dispatch("loadWorkspace", {
          id: res.id,
          client: true,
        });

        await signalr.setupCommunication(clientStore.state.workspace.id);
        if (this.$route.name != "dashboard")
          this.$router.push({ name: "dashboard" });
      }
    },
    async showContextOptions() {
      this.workspaceContextSearch = "";
      this.contextOptionList = await clientStore.dispatch(
        "getWorkspaceContexts",
        {
          search: this.workspaceContextSearch,
        }
      );

      this.$nextTick(() => {
        this.$refs.workspaceContextSearch.focus();
      });
    },
    selectWorkspaceContext(res) {
      clientStore.dispatch("setWorkspaceContext", res);
      window.eventBus.$emit("ftsListViewRefresh");
    },
    async infiniteHandler($state) {
      const res = await this.loadNotification();
      if (res) {
        this.notifCurrentPage += 1;
        $state.loaded();
      } else {
        $state.complete();
      }
    },
    async openQuickView(item) {
      window.eventBus.$emit("quickViewData", {
        items: [{ id: item.locusId }],
      });
    },
    quickViewRefresh(item) {
      if (
        this.$route.name === "list-view" &&
        item &&
        item.moduleId == this.moduleId
      ) {
        window.eventBus.$emit("ftsListViewRefresh");
      } else {
        window.eventBus.$emit("quickViewRefresh");
      }
    },
    refresh() {
      this.broadcastNotification = false;
      location.reload();
    },
    /* notification */
    async openNotification() {
      // reset notifications
      this.notifications = [];
      this.notifCurrentPage = 0;
      // clear unreceive count
      await clientStore.dispatch("clearNotificationUnreceivedCount");
      this.unreadCount = 0;
    },
    async loadNotification() {
      try {
        const res = await clientStore.dispatch("retrieveNotification", {
          pageSize: 10,
          currentPage: this.notifCurrentPage,
        });

        if (res.data && res.data.list && res.data.list.length) {
          let groupedList = [];

          function setGroup(data) {
            return {
              id: commons.nanoID(),
              vectorId: data[0].vectorId,
              list: [...data],
              read: data.every(x => x.read),
              isGroup: true,
            }
          };

          res.data.list.reduce((list, x, ind, arr) => {
            /* set short message */
            x.shortMessage = _.truncate(x.message, {
              'length': 200,
            });
            /* grouping */
            if (groupedList.length) {
              if (groupedList[0].vectorId == x.vectorId) {
                if (groupedList.findIndex(y => y.id === x.id) === -1)
                  groupedList.push(x);
              } else if (groupedList.length >= 3) {
                list.push(setGroup(groupedList));
                groupedList = new Array(x);
              } else {
                list = list.concat(groupedList);        
                groupedList = new Array(x);
              }
            } else {
              groupedList.push(x);
            }
            /* last item */
            if (arr.length === ind + 1) {
              if (groupedList.length >= 3) {
                list.push(setGroup(groupedList));
              } else {
                list = list.concat(groupedList);
              }
              groupedList = [];
            }
            return list;
          }, []).forEach((x) => {
            if (this.notifications.findIndex(y => y.id === x.id) === -1) {
              this.notifications.push(x);
            }
          });
          return true;
        }
        return false;
      } catch (err) {
        console.error(err);
        return false;
      }
    },
    toggleNotificationGroup(item) {
      if (item.expand)
        this.$delete(item, 'expand');
      else
        this.$set(item, 'expand', true);
    },
    notificationGroupUnreadCount(list) {
      const count = list.filter(x => !x.read).length;
      
      if (count) {
        if (count === 1)
          return '1 notification';
        else
          return `${count} notifications`;
      }
    },
    async loadNotificationCount() {
      let res2 = await clientStore.dispatch("retrieveNotificationCount");
      this.unreadCount = res2.data && res2.data.count;
    },
  },
  watch: {
    workspaceSearch: _.debounce(async function () {
      this.workspaceOptionList = await clientStore.dispatch("getWorkspaces", {
        search: this.workspaceSearch,
      });
    }, 500),
    workspaceContextSearch: _.debounce(async function () {
      this.contextOptionList = await clientStore.dispatch(
        "getWorkspaceContexts",
        {
          search: this.workspaceContextSearch,
        }
      );
    }, 500),
  },
  computed: {
    sidenavLogo() {
      return authorizationStore.getters.appLogo;
    },
    studioRoute() {
      let route = {
        name: 'studio',
        query: {}
      }

      if (this.module.id) 
        route.query.id = commons.encodeids(this.module.id);
      else
        route.query.wsId = this.workspaceId;

      return route;
    },
    routePath() {
      return `${this.$route.path}/${this.$route.query.l}`;
    },
    workspace() {
      return clientStore.state.workspace || {};
    },
    workspaceId() {
      return clientStore.getters.workspaceId;
    },
    workspaceContext() {
      return clientStore.state.workspaceContext || {};
    },
    module() {
      return clientStore.state.module || {};
    },
    moduleId() {
      return clientStore.getters.moduleId;
    },
    list() {
      return clientStore.state.list || {};
    },
    profile() {
      return authorizationStore.getters.userProfile;
    },
    userRoles() {
      return authorizationStore.getters.userRoles;
    },
    isAdminUser() {
      return authorizationStore.getters.isAdminUser;
    },
    isManagerUser() {
      return authorizationStore.getters.isManagerUser;
    },
    isInternal() {
      return authorizationStore.getters.isInternal;
    },
  },
  async mounted() {
    await webAppConfig.getConfig();
    
    let vm = this;
    $cookies.set("loggedSource", "client");
    /* prefers color scheme */
    function setTheme(darkTheme) {
      if (darkTheme) {
        document.body.classList.add('dark');
        localStorage.setItem("fetias-client-theme", 'dark');
      } else {
        document.body.classList.remove('dark');
        localStorage.setItem("fetias-client-theme", 'light');
      }
    }
    window
      .matchMedia('(prefers-color-scheme: dark)')
      .addEventListener('change', event => {  
        if (Boolean(localStorage["fetias-client-theme-sync-os"])) {
          setTheme(event.matches);
        }
      });
    
    if (Boolean(localStorage["fetias-client-theme-sync-os"])) {
      setTheme(window.matchMedia('(prefers-color-scheme: dark)').matches);
    }
    /* appearance */
    else if (localStorage['fetias-client-theme'] && localStorage['fetias-client-theme'] === 'dark') {
      setTheme(true);
    }
    /* color */
    const color = localStorage['fetias-client-color'];
    if (color) {
      try {
        document.body.getElementsByClassName('fts-client')[0].classList.add(color);
      } catch(err) {
        console.warn('ClientColor', err);
      }
    }
    /* profile */
    await authorizationStore.dispatch("profile");
    /* pdf document */
    window.eventBus.$on("showPDFDocument", async (payload) => {
      let vm = this;
      let parameters = {
        documentId: payload.document.id,
        vectorId: payload.moduleId,
        locusIds:
          payload.items && payload.items.length
            ? payload.items.map((x) => x.id)
            : null,
        locusId:
          payload.items && payload.items.length ? payload.items[0].id : null,
        list: payload.listingId,
        queryFilters: payload.filters,
        inputs: payload.inputs,
      };

      let prefixPDFUrl =
        "/service/PDFTemplateProcessing/document?documentModel=";
      let documentModel = { parameters: parameters };
      let prefixHTMLUrl =
        "/service/HTMLTemplateProcessing/document?documentModel=";
      classificationStore.dispatch("updateDocumentId", null);

      if (
        payload.document.typeId == "spreadsheet" ||
        payload.document.typeId == "file"
      ) {

        function addListeners(xhr) {
          xhr.addEventListener('loadstart', () => {
            vm.$toasted.info("Preparing download");
          });
        }

        documentModel.outputType = 4;
        let url = prefixPDFUrl + encodeURIComponent(JSON.stringify(documentModel));
        var xhr = new XMLHttpRequest();
        addListeners(xhr);
        xhr.open(
          "GET",
          webAppConfig.baseURL ? webAppConfig.baseURL + url : url,
          true
        );
        var token = JSON.parse(localStorage.getItem("ls.authorizationData"))[
          "access_token"
        ];
        xhr.setRequestHeader(
          "Content-type",
          "application/x-www-form-urlencoded"
        );
        xhr.setRequestHeader("authorization", "bearer " + token);
        xhr.setRequestHeader("wsId", this.workspaceId);
        xhr.responseType = "arraybuffer";
        xhr.onload = function () {
          if (this.status === 200) {
            var filename = "";
            var disposition = xhr.getResponseHeader("Content-Disposition");
            if (disposition && disposition.indexOf("attachment") !== -1) {
              var filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
              var matches = filenameRegex.exec(disposition);
              if (matches != null && matches[1])
                filename = matches[1].replace(/['"]/g, "");
            }
            var type = xhr.getResponseHeader("Content-Type");

            var blob;
            if (typeof File === "function") {
              try {
                blob = new File([this.response], filename, { type: type });
              } catch (e) {
                /* Edge */
              }
            }
            if (typeof blob === "undefined") {
              blob = new Blob([this.response], { type: type });
            }

            if (typeof window.navigator.msSaveBlob !== "undefined") {
              // IE workaround for "HTML7007: One or more blob URLs were revoked by closing the blob for which they were created. These URLs will no longer resolve as the data backing the URL has been freed."
              window.navigator.msSaveBlob(blob, filename);
            } else {
              var URL = window.URL || window.webkitURL;
              var downloadUrl = URL.createObjectURL(blob);

              if (filename) {
                // use HTML5 a[download] attribute to specify filename
                var a = document.createElement("a");
                // safari doesn't support this yet
                if (typeof a.download === "undefined") {
                  window.location = downloadUrl;
                } else {
                  a.href = downloadUrl;
                  a.download = filename;
                  document.body.appendChild(a);
                  a.click();
                }
              } else {
                window.location = downloadUrl;
              }

              setTimeout(function () {
                URL.revokeObjectURL(downloadUrl);
              }, 100); // cleanup
            }
          } else {
            vm.$toasted.global.error();
            console.error(`${xhr.status} (${xhr.statusText})`);
          }
        };
        xhr.send();
      } else if (payload.document.typeId == "pdf") {
        let url =
          prefixPDFUrl + encodeURIComponent(JSON.stringify(documentModel));
        // window.open(
        //   "/pi/pdfjs/web/viewer.html?file=" + encodeURIComponent(url)
        // );
        vm.$refs.documentModal.show({
          url: url,
          parameters: {
            moduleId: payload.moduleId,
            templateId: payload.document.id,
            template: payload.document.template,
            entryId: payload.items && payload.items.length ? payload.items[0].id : null,
            entryIds: payload.items && payload.items.length
            ? payload.items.map((x) => x.id)
            : null,
            listingId: payload.listingId,
            filters: payload.filters,
            inputs: payload.inputs,
          }
        });
      } else {
        const encodedUri = prefixHTMLUrl + JSON.stringify(documentModel);
        const url = `/#/htmlViewer?file=${encodeURIComponent(encodedUri)}`;
        window.open(url, "_blank");
      }
    });
    /* notification */
    window.eventBus.$on("refreshNotification", async () => {
      let res3 = await clientStore.dispatch("retrieveNotificationCount");
      vm.unreadCount = res3 && res3.data && res3.data.count;
    });
    await this.loadNotificationCount();
    /* load workspace */
    let workspaceErr = false;
    async function loadWorkspace() {
      try {
        await clientStore.dispatch("loadWorkspace", {
          client: true,
        });
        await signalr.setupCommunication(clientStore.state.workspace.id);
        workspaceErr = false;
      } catch (err) {
        if (err.response && err.response.data && err.response.data.message) {
          console.warn(err.response.data.message);
        } else {
          console.warn("unable load workspace");
        }
        workspaceErr = true;
      }
    }
    await loadWorkspace();
    /* attempts load workspace */
    if (workspaceErr) {
      console.warn("attempts load workspace");
      await loadWorkspace();
    }
    /* pre-workspaces */
    const preWorkspaces = await clientStore.dispatch("getWorkspaces", {
      search: "",
    });
    this.preWorkspaces = [
      ...[{ id: this.workspace.id, name: this.workspace.name }],
      ...preWorkspaces.filter((x) => x.id != this.workspaceId).slice(0, 2),
    ].map((x) => ({
      id: x.id,
      name: x.name,
      shortName: x.name && x.name.substring(0, 2).toLowerCase(),
    }));
    /* loaded */
    this.loaded = true;
    /* module changed */
    window.eventBus.$on('moduleChanged', (changes) => {
      vm.broadcastNotification = true;
    });
  },
  beforeDestroy() {
    window.eventBus.$off('showPDFDocument');
    window.eventBus.$off('refreshNotification');
    window.eventBus.$off('moduleChanged');
  },
};
</script>